// Saving projects and merging two people's changes

// Settings from when budgets lived in Google Sheets. New projects don't have them; older copies (in this
// browser, or in a backup) may, so they're dropped whenever a project is saved.
const OLD_SHEET_KEYS = ['connMode', 'gasUrl', 'gasToken', 'apiKey', 'sheetId', 'sheetTitle', 'connected', 'lastFetch', 'sheetTab'];
export const J = x => JSON.stringify(x === undefined ? null : x);
export const rid = p => p + Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4);
export function stripLocal(p) {
  const o = { ...p };
  OLD_SHEET_KEYS.forEach(k => delete o[k]);
  return o;
}
// Stable ids on every section and line, so invoices, quotes and colleagues' edits can find them.
// Each line also has a number, _sheetRow, unique within the project: invoices and quotes refer to lines by it
// (allocation.row). The name is from the spreadsheet days; it is kept because saved projects, invoices and
// backups all use it, and renaming it would mean migrating them for no visible change.
export function normalizeProject(p) {
  if (!p) return p;
  let seq = Number(p._seq) || 0, changed = false;
  const secs = p.sections || [];
  secs.forEach(s => { seq = Math.max(seq, Number(s._headerRow) || 0); (s.items || []).forEach(it => { seq = Math.max(seq, Number(it._sheetRow) || 0); }); });
  const usedRows = new Set(), usedIds = new Set();
  const sections = secs.map(s => {
    let ns = s;
    const fix = patch => { ns = ns === s ? { ...s, ...patch } : Object.assign(ns, patch); changed = true; };
    if (!s.id || usedIds.has(s.id)) fix({ id: rid('sec_') });
    usedIds.add(ns.id);
    if (!s._headerRow || usedRows.has(s._headerRow)) fix({ _headerRow: ++seq });
    usedRows.add(ns._headerRow);
    let itemsChanged = false;
    const items = (s.items || []).map(it => {
      let ni = it;
      const fixI = patch => { ni = ni === it ? { ...it, ...patch } : Object.assign(ni, patch); itemsChanged = true; };
      if (!it.id || usedIds.has(it.id)) fixI({ id: rid('it_') });
      usedIds.add(ni.id);
      if (!it._sheetRow || usedRows.has(it._sheetRow)) fixI({ _sheetRow: ++seq });
      usedRows.add(ni._sheetRow);
      if (!it.lineId) fixI({ lineId: 'L' + rid('').slice(0, 10) });
      return ni;
    });
    if (itemsChanged) fix({ items });
    return ns;
  });
  if (!changed && (Number(p._seq) || 0) >= seq) return p;
  return { ...p, sections, _seq: Math.max(seq, Number(p._seq) || 0) };
}
// Postgres returns JSON with its own key order, so merges compare with keys sorted.
const canonO = x => Array.isArray(x) ? x.map(canonO) : (x && typeof x === 'object') ? Object.keys(x).sort().reduce((o, k) => { o[k] = canonO(x[k]); return o; }, {}) : x;
const CJ = x => JSON.stringify(canonO(x === undefined ? null : x));
// Lines both people changed at the same time (the merge keeps this person's version)
export function lineClashes(b, m, t) {
  const idx = d => new Map((d?.sections || []).flatMap(s => (s.items || []).map(it => [it.id, it])));
  const B = idx(b), M = idx(m), Tt = idx(t), out = [];
  const LABEL = { desc: 'description', qty: 'quantity', up: 'unit price', mu: 'markup', vat: 'VAT', st: 'status', sup: 'supplier', ccy: 'currency', note: 'note', unit: 'unit', ag: 'actual', ab: 'billed' };
  M.forEach((mi, id) => {
    const bi = B.get(id), ti = Tt.get(id);
    if (!bi || !ti || CJ(mi) === CJ(bi) || CJ(ti) === CJ(bi) || CJ(mi) === CJ(ti)) return;
    // lines merge field by field, so only a field both people changed (to different values) loses theirs
    const fields = Object.keys(LABEL).filter(k => CJ(mi[k]) !== CJ(ti[k]) && CJ(ti[k]) !== CJ(bi[k]) && CJ(mi[k]) !== CJ(bi[k]));
    if (fields.length) out.push({ desc: mi.desc || ti.desc || 'a line', theirs: fields.map(k => `${LABEL[k]} ${k === 'mu' || k === 'vat' ? Math.round((Number(ti[k]) || 0) * 1000) / 10 + '%' : ti[k] ?? '—'}`) });
  });
  return out;
}
// Three-way merge used when two people saved the same project: changes to different lines both survive.
// Order: theirs, with what only I have added at the end; but when only I changed the order (moved a line), mine.
function mergeById(b = [], m = [], t = [], inner) {
  const bi = new Map(b.map(x => [x.id, x])), mi = new Map(m.map(x => [x.id, x])), ti = new Map(t.map(x => [x.id, x]));
  const ids = l => l.map(x => x.id).join('\u0001');
  const order = ids(t) === ids(b) && ids(m) !== ids(b)
    ? [...m.map(x => x.id), ...t.map(x => x.id).filter(id => !mi.has(id))]
    : [...t.map(x => x.id), ...m.map(x => x.id).filter(id => !ti.has(id))];
  const out = [];
  for (const id of order) {
    const B = bi.get(id), M = mi.get(id), T = ti.get(id);
    if (M && !B) { out.push(T ? (inner ? inner(undefined, M, T) : M) : M); continue; }
    if (B && !M) continue;
    if (B && M && !T) { if (CJ(M) !== CJ(B)) out.push(M); continue; }
    if (!M) { if (!B) out.push(T); continue; }
    out.push(inner ? inner(B, M, T) : (CJ(M) !== CJ(B) ? M : T));
  }
  return out;
}
function mergeFields(b = {}, m = {}, t = {}, skip = []) {
  const out = { ...t };
  for (const k of new Set([...Object.keys(m), ...Object.keys(b)])) {
    if (skip.includes(k)) continue;
    if (CJ(m[k]) !== CJ(b[k])) { if (k in m) out[k] = m[k]; else delete out[k]; }
  }
  return out;
}
export function merge3(b, m, t) {
  const out = mergeFields(b, m, t, ['sections', 'deadlines', 'subBudgets', 'approvals', 'pos']);
  // deadlines, client approvals and purchase orders, like lines: one person ticking one off while another adds a
  // new one keeps both
  for (const k of ['deadlines', 'approvals', 'pos']) if (b[k] || m[k] || t[k]) out[k] = mergeById(b[k], m[k], t[k]);
  // sub-budgets and their lines, the same way
  if (b.subBudgets || m.subBudgets || t.subBudgets) out.subBudgets = mergeById(b.subBudgets, m.subBudgets, t.subBudgets, (B, M, T) => {
    const s = mergeFields(B || {}, M, T, ['lines']);
    s.lines = mergeById(B ? B.lines : [], M.lines, T.lines);
    return s;
  });
  out.sections = mergeById(b.sections, m.sections, t.sections, (B, M, T) => {
    const s = mergeFields(B || {}, M, T, ['items']);
    s.items = mergeById(B ? B.items : [], M.items, T.items);
    return s;
  });
  return mergeMovedLines(b, m, t, out);
}
// Lines merge field by field (one person renumbering or moving a line while the other changes its price keeps
// both), and a line moved to another section by one person appears once: where it was moved to.
function mergeMovedLines(b, m, t, out) {
  const where = d => { const w = new Map(); (d?.sections || []).forEach(s => (s.items || []).forEach(it => w.set(it.id, { s: s.id, it }))); return w; };
  const B = where(b), M = where(m), T = where(t);
  // the section each line stays in: where it was moved to, if it shows up in more than one
  const home = new Map();
  out.sections.forEach(s => (s.items || []).forEach(it => {
    const prev = home.get(it.id);
    if (!prev || (prev === B.get(it.id)?.s && s.id !== prev)) home.set(it.id, s.id);
  }));
  const placed = new Set();
  out.sections = out.sections.map(s => ({ ...s, items: renumberIfMixed((s.items || [])
    .filter(it => home.get(it.id) === s.id && !placed.has(it.id) && placed.add(it.id))
    .map(it => B.has(it.id) && M.has(it.id) && T.has(it.id) ? mergeFields(B.get(it.id).it, M.get(it.id).it, T.get(it.id).it) : it)) }));
  return out;
}
// When both people reordered a section (one moved a line, the other added or moved another), the merged order is
// theirs while some numbers are mine: renumber that section 1, 2, 3… so no two lines share a number. Sections whose
// numbers still run in order (gaps from deleted lines included) are left as they are.
function renumberIfMixed(items) {
  if (items.some(it => it.ln == null || it.ln === '')) return items;
  const ok = items.every((it, i) => i === 0 || Number(it.ln) > Number(items[i - 1].ln));
  return ok ? items : items.map((it, i) => it.ln === i + 1 ? it : { ...it, ln: i + 1 });
}
